import React, { useState } from "react";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { SksdStatus } from "../../types/types";
import { resendSksdLog } from "../../utils/sksdUtils";
import { toast } from "react-toastify";
import Graphic from "@arcgis/core/Graphic";
import "./SksdLog.css";
import { Popover } from "react-tiny-popover";
import OptionsIcon from "../../assets/options.svg?react";

interface SksdLogItemProps {
    log: Graphic;
    isSksdAdmin: boolean;
    onShowXml: (log: Graphic, response: boolean) => void;
    onReload: () => void;
}

const SksdLogItem: React.FC<SksdLogItemProps> = ({ log, isSksdAdmin, onShowXml, onReload }) => {
    const [resending, setResending] = useState(false);
    const [optionsPopoverOpen, setOptionsPopoverOpen] = useState(false);

    const statusText = (() => {
        const raw = log.attributes.status as unknown;
        if (raw === null || raw === undefined) return "";
        const num = typeof raw === "number" ? raw : Number(raw);
        const text = Number.isFinite(num) ? (SksdStatus as unknown as Record<number, string>)[num] : String(raw);
        return text ?? String(raw);
    })();

    const handleResend = async (regenerate: boolean) => {
        if (resending) return;
        const progressToastId = "sksd-resend-progress";
        setResending(true);
        if (regenerate) {
            toast.info("Genererer ny XML og sender på nytt...", {
                toastId: progressToastId,
                progress: 0.1,
                autoClose: false,
            });
        } else {
            toast.info("Sender XMLen på nytt...", {
                toastId: progressToastId,
                progress: 0.5,
                autoClose: false,
            });
        }

        try {
            const result = await resendSksdLog(log.attributes.navn, regenerate);
            if (!result.success) {
                toast.error("Feil ved sending til SKSD: " + (result.message || "Ukjent feil"), {
                    autoClose: false,
                    className: "toast-error",
                });
            } else {
                toast.success("Melding sendt til SKSD på nytt");
                toast.update(progressToastId, {
                    render: "Melding sendt til SKSD på nytt",
                    type: "success",
                    progress: 1,
                    autoClose: 5000,
                });
            }
            onReload();
        } catch (error) {
            toast.update(progressToastId, {
                render: "Feil ved sending til SKSD: " + (error instanceof Error ? error.message : String(error)),
                type: "error",
                progress: 1,
                autoClose: false,
            });
        } finally {
            setResending(false);
            setOptionsPopoverOpen(false);
        }
    };

    return (
        <div className="sksd-log-entry">
            <div className="sksd-log-entry-header">
                <strong>{log.attributes.navn}</strong>
                {isSksdAdmin && (
                    <Popover
                        containerStyle={{ zIndex: "12" }}
                        positions={["left"]}
                        isOpen={optionsPopoverOpen}
                        onClickOutside={() => setOptionsPopoverOpen(false)}
                        content={
                            <div className="sksd-log-entry-header-popover-content">
                                <NfsButton
                                    onClick={() => handleResend(false)}
                                    outlined
                                    disabled={resending}
                                    className="sksd-log-pagination-button"
                                >
                                    {resending ? "Sender..." : "Send XMLen på nytt"}
                                </NfsButton>
                                <NfsButton
                                    onClick={() => handleResend(true)}
                                    outlined
                                    disabled={resending}
                                    className="sksd-log-pagination-button"
                                >
                                    {resending ? "Sender..." : "Generer ny XML og send"}
                                </NfsButton>
                            </div>
                        }
                    >
                        <button
                            type="button"
                            className="empty-button"
                            onClick={() => {
                                setOptionsPopoverOpen(!optionsPopoverOpen);
                            }}
                        >
                            <OptionsIcon />
                        </button>
                    </Popover>
                )}
            </div>

            <span>{new Date(log.attributes.dato).toLocaleString("no")}</span>
            <span>{log.attributes.epost}</span>
            <div className="sksd-log-entry-status-row">
                <span>{statusText}</span>
                <NfsButton
                    onClick={() => onShowXml(log, false)}
                    outlined
                    className="sksd-log-entry-show-xml-button"
                    small
                >
                    Vis melding
                </NfsButton>
            </div>
        </div>
    );
};

export default SksdLogItem;
